import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getCollectionQuery, baseQueryAuth } from "../../reduxStore/helper";

/**
 * Route sort order (spec `vehicle-realtime-data-consistency`, design "Route
 * Loading Strategy" / task 3.10).
 *
 * The route endpoints (`GET /formatted/devices/{deviceId}` and
 * `GET /formatted/vehicles/{plateNumber}`) return packets sorted by
 * `source_time`. The live summary must load the *newest* page first so a range
 * with `total_count > per_page` cannot hide the newest packet on an ascending
 * page zero (#119201 / bugfix.md 1.7 / 2.7). The live table is the default
 * caller, so these endpoints default to `DESC` (newest page first); the
 * ascending order opt back in with `sortOrder: "ASC"`. History tables use
 * descending order so server-side pagination remains globally newest-first.
 */
export const ROUTE_SORT_ASC = "source_time:ASC";
export const ROUTE_SORT_DESC = "source_time:DESC";

/**
 * Resolves the `sort` query value from a caller-supplied `sortOrder`.
 * Accepts `"ASC"`/`"DESC"` (case-insensitive) or a full `source_time:*` token.
 * Defaults to descending (newest page first) so the live summary can never hide
 * the newest packet; callers that page through the range ascending pass `"ASC"`.
 *
 * @param {string} [sortOrder] - `"ASC"`, `"DESC"`, or a `source_time:*` token.
 * @returns {string} The `source_time:ASC` / `source_time:DESC` sort value.
 */
export const resolveRouteSort = (sortOrder) => {
  if (typeof sortOrder !== "string" || sortOrder === "") return ROUTE_SORT_DESC;
  const normalized = sortOrder.trim().toUpperCase();
  if (normalized === "ASC" || normalized === ROUTE_SORT_ASC.toUpperCase()) {
    return ROUTE_SORT_ASC;
  }
  return ROUTE_SORT_DESC;
};

/**
 * Builds the URL for `getVehicleRouting` (devices route endpoint). Extracted as a
 * pure function so the sort/pagination contract can be unit tested without the
 * RTK Query runtime.
 *
 * @param {Object} queryParams
 * @returns {string} The relative request URL.
 */
export const buildVehicleRoutingUrl = (queryParams = {}) => {
  const type = queryParams?.type;

  const qParam =
    type === "Vehicles" &&
    queryParams?.plate_number &&
    encodeURIComponent(
      JSON.stringify({
        $and: [
          {
            plate_number: {
              $in: [queryParams.plate_number],
            },
          },
        ],
      })
    );

  return `${queryParams.deviceId}?packet_types=device_data${
    queryParams.isTime
      ? `&start_time=${queryParams.start_time}&end_time=${queryParams.end_time}`
      : ""
  }&per_page=${queryParams.per_page}&page=${queryParams.page}${
    qParam ? `&q=${qParam}` : ""
  }&sort=${resolveRouteSort(queryParams?.sortOrder)}`;
};

/**
 * Builds the URL for `getFormattedVehicleRoute` (vehicles route endpoint).
 *
 * @param {Object} queryParams
 * @returns {string} The relative request URL.
 */
export const buildFormattedVehicleRouteUrl = (queryParams = {}) =>
  `${queryParams.plate_number}?packet_types=device_data${
    queryParams.isTime
      ? `&start_time=${queryParams.start_time}&end_time=${queryParams.end_time}`
      : ""
  }&per_page=${queryParams.per_page}&page=${queryParams.page}&sort=${resolveRouteSort(
    queryParams?.sortOrder
  )}`;

export const trackingApi = createApi({
  name: "trackingApi",
  reducerPath: "trackingApi",
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_TRACKING_API + "/formatted/latest",
    prepareHeaders: (headers, { getState }) => {
      const authToken = sessionStorage.getItem("fmdp-access-token");
      if (authToken) {
        headers.set("Authorization", authToken);
      }
      return headers;
    },
  }),
  tagTypes: ["devices"],
  endpoints: (builder) => ({
    getDevicesTracking: builder.query({
      query: (queryParams) => {
        let queryParam = [];
        if (queryParams?.filter && queryParams?.q !== "") {
          queryParam = JSON.stringify({
            $or: [
              {
                "device_data.other_data.device_name.v": {
                  $like: queryParams.q || "",
                },
              },
              {
                "device_data.plate_number.v": { $like: queryParams.q || "" },
              },
              {
                "heartbeat.other_data.device_name.v": {
                  $like: queryParams.q || "",
                },
              },
              {
                "device_reply.other_data.device_name.v": {
                  $like: queryParams.q || "",
                },
              },
              {
                "login.other_data.device_name.v": {
                  $like: queryParams.q || "",
                },
              },
              {
                "disconnect.other_data.device_name.v": {
                  $like: queryParams.q || "",
                },
              },
              {
                "registration.other_data.device_name.v": {
                  $like: queryParams.q || "",
                },
              },
              {
                "device_data.source_id": {
                  $like: queryParams.q || "",
                },
              },
              {
                "heartbeat.source_id": {
                  $like: queryParams.q || "",
                },
              },
              {
                "registration.source_id": {
                  $like: queryParams.q || "",
                },
              },
              {
                "disconnect.source_id": {
                  $like: queryParams.q || "",
                },
              },
              {
                "login.source_id": {
                  $like: queryParams.q || "",
                },
              },
              {
                "device_reply.source_id": {
                  $like: queryParams.q || "",
                },
              },
            ],
          });
        }
        return {
          url: `devices?${
            queryParams.device_ids ? `device_ids=${queryParams.device_ids}&` : ""
          }page=${queryParams.page}&per_page=${queryParams.per_page}&plate_number=${
            queryParams.plate_number
          }`,
          method: "GET",
          params: {
            q: queryParam,
          },
        };
      },
    }),
  }),
});

export const trackingVehicleApi = createApi({
  name: "trackingVehicleApi",
  reducerPath: "trackingVehicleApi",
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_TRACKING_API + "/formatted/latest",
    prepareHeaders: (headers, { getState }) => {
      const authToken = sessionStorage.getItem("fmdp-access-token");
      if (authToken) {
        headers.set("Authorization", authToken);
      }
      return headers;
    },
  }),
  // tagTypes: ["vehicles"],
  endpoints: (builder) => ({
    getVehiclesTracking: builder.query({
      query: (queryParams) => {
        let queryParam = [];
        if (queryParams?.filter) {
          queryParam = JSON.stringify({
            $or: [
              { source_id: { $like: queryParams.q || "" } },
              { "plate_number.v": { $like: queryParams.q || "" } },
              { "other_data.device_name": { $like: queryParams.q || "" } },
            ],
          });
        }
        return {
          url: `vehicles?${
            queryParams.plate_numbers ? `plate_numbers=${queryParams.plate_numbers}&` : ""
          }page=${queryParams.page}&per_page=${queryParams.per_page}`,
          method: "GET",
          params: {
            q: queryParam,
            ...(queryParams.sort && { sort: queryParams.sort }),
          },
        };
      },
    }),
  }),
});

export const reverseGeoCodingApi = createApi({
  name: "reverseGeoCodingApi",
  reducerPath: "reverseGeoCodingApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_REVERSE_GEO_CODING_API,
    prepareHeaders: (headers, { getState }) => {
      const authToken = sessionStorage.getItem("fmdp-access-token");
      if (authToken) {
        headers.set("Authorization", authToken);
      }
      return headers;
    },
  }),
  tagTypes: ["reverse"],
  endpoints: (builder) => ({
    getAddress: getCollectionQuery(builder, "reverse"),
  }),
});

export const vehicleRouteApi = createApi({
  name: "vehicleRouteApi",
  reducerPath: "vehicleRouteApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_TRACKING_API + "/formatted/devices/",
    prepareHeaders: (headers, { getState }) => {
      const authToken = sessionStorage.getItem("fmdp-access-token");
      if (authToken) {
        headers.set("Authorization", authToken);
      }
      return headers;
    },
  }),
  tagTypes: ["route"],
  endpoints: (builder) => ({
    getVehicleRouting: builder.query({
      query: (queryParams) => {
        return {
          url: buildVehicleRoutingUrl(queryParams),
          method: "get",
        };
      },
      keepUnusedDataFor: 2,
      providesTags: ["route"],
    }),
  }),
});

export const formattedVehicleRouteApi = createApi({
  name: "formattedVehicleRouteApi",
  reducerPath: "formattedVehicleRouteApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_TRACKING_API + "/formatted/vehicles/",
    prepareHeaders: (headers, { getState }) => {
      const authToken = sessionStorage.getItem("fmdp-access-token");
      if (authToken) {
        headers.set("Authorization", authToken);
      }
      return headers;
    },
  }),
  tagTypes: ["route"],
  endpoints: (builder) => ({
    getFormattedVehicleRoute: builder.query({
      query: (queryParams) => {
        return {
          url: buildFormattedVehicleRouteUrl(queryParams),
          method: "get",
        };
      },
      keepUnusedDataFor: 2,
      providesTags: ["route"],
    }),
  }),
});

//Trips API
export const tripsApi = createApi({
  name: "tripsApi",
  reducerPath: "tripsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_TRACKING_API + "/trips",
    prepareHeaders: (headers, { getState }) => {
      const authToken = sessionStorage.getItem("fmdp-access-token");
      if (authToken) {
        headers.set("Authorization", authToken);
      }
      return headers;
    },
  }),
  tagTypes: ["trips"],
  endpoints: (builder) => ({
    getTrips: builder.query({
      query: (queryParams) => {
        let search = queryParams?.search;
        const type = queryParams?.type;
        // If search is false then search params are not sent
        let qParam = queryParams?.isAdmin
          ? search &&
            encodeURIComponent(
              JSON.stringify({
                $or: [
                  {
                    plate_number: {
                      $like: queryParams.q,
                    },
                  },
                  {
                    driver_name: {
                      $like: queryParams.q,
                    },
                  },
                  {
                    source_id: {
                      $like: queryParams.q,
                    },
                  },
                  {
                    device_name: {
                      $like: queryParams.q,
                    },
                  },
                  {
                    status: {
                      $like: queryParams.q,
                    },
                  },
                ],
              })
            )
          : search
          ? encodeURIComponent(
              JSON.stringify({
                $and: [
                  {
                    plate_number: {
                      $in: queryParams.plate_number,
                    },
                  },
                ],
              })
            )
          : type === "Vehicles"
          ? encodeURIComponent(
              JSON.stringify({
                $and: [
                  {
                    plate_number: {
                      $in: queryParams.plate_number,
                    },
                  },
                ],
              })
            )
          : encodeURIComponent(
              JSON.stringify({
                $and: [
                  {
                    source_id: {
                      $in: queryParams.source_id,
                    },
                  },
                ],
              })
            );
        return {
          url: `?start_time=${queryParams.startTime}&end_time=${queryParams.endTime}&per_page=${
            queryParams.per_page
          }&page=${queryParams.page}&client_id=${queryParams.clientId}&q=${
            queryParams.q || queryParams.plate_number || queryParams.source_id ? qParam : ""
          }`,
          method: "get",
        };
      },
      keepUnusedDataFor: 2,
      providesTags: ["trips"],
    }),
  }),
});

//Activities API
export const tripsTrackApi = createApi({
  name: "tripsTrackApi",
  reducerPath: "tripsTrackApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_TRACKING_API + "/activities",
    prepareHeaders: (headers, { getState }) => {
      const authToken = sessionStorage.getItem("fmdp-access-token");
      if (authToken) {
        headers.set("Authorization", authToken);
      }
      return headers;
    },
  }),
  tagTypes: ["tripsTrack"],
  endpoints: (builder) => ({
    getTripsTrack: builder.query({
      query: (queryParams) => {
        const search = queryParams?.search;
        const type = queryParams?.type;
        // If search is false then search params are not sent
        let qParam = queryParams?.isAdmin
          ? search &&
            encodeURIComponent(
              JSON.stringify({
                $or: [
                  {
                    plate_number: {
                      $like: queryParams.q,
                    },
                  },
                  {
                    modes: {
                      $like: queryParams.q,
                    },
                  },
                  {
                    source_id: {
                      $like: queryParams.q,
                    },
                  },
                  {
                    device_name: {
                      $like: queryParams.q,
                    },
                  },
                  {
                    driver_name: {
                      $like: queryParams.q,
                    },
                  },
                  {
                    status: {
                      $like: queryParams.q,
                    },
                  },
                ],
              })
            )
          : search
          ? encodeURIComponent(
              JSON.stringify({
                $and: [
                  {
                    plate_number: {
                      $in: queryParams.plate_number,
                    },
                  },
                ],
              })
            )
          : type === "Vehicles"
          ? encodeURIComponent(
              JSON.stringify({
                $and: [
                  {
                    plate_number: {
                      $in: queryParams.plate_number,
                    },
                  },
                ],
              })
            )
          : encodeURIComponent(
              JSON.stringify({
                $and: [
                  {
                    source_id: {
                      $in: queryParams.source_id,
                    },
                  },
                ],
              })
            );
        function vehicleStatus() {
          if (queryParams.q === "TRIP") {
            return "TRIP";
          } else {
            if (queryParams.q === "PARKED") {
              return "PARKED";
            } else {
              return "TRIP%2CPARKED";
            }
          }
        }
        return {
          url: `?start_time=${queryParams.startTime}&end_time=${queryParams.endTime}&per_page=${
            queryParams.per_page
          }&page=${queryParams.page}&client_id=${queryParams.clientId}&modes=${vehicleStatus()}&q=${
            queryParams.q || queryParams.plate_number || queryParams.source_id ? qParam : ""
          }`,
          method: "get",
        };
      },
      keepUnusedDataFor: 2,
      providesTags: ["tripsTrack"],
    }),
  }),
});

//Events API
export const eventsApi = createApi({
  name: "eventsApi",
  reducerPath: "eventsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_TRACKING_API + "/events",
    prepareHeaders: (headers, { getState }) => {
      const authToken = sessionStorage.getItem("fmdp-access-token");
      if (authToken) {
        headers.set("Authorization", authToken);
      }
      return headers;
    },
  }),
  tagTypes: ["events"],
  endpoints: (builder) => ({
    getEvents: builder.query({
      query: (queryParams) => {
        const search = queryParams?.search;
        const type = queryParams?.type;
        const advanceSearch = queryParams?.advanceSearch;

        // Handle advanced search if provided
        if (advanceSearch && Object.keys(advanceSearch).length > 0) {
          const advanceSearchConditions = [];

          if (advanceSearch.plate_number_cont) {
            advanceSearchConditions.push({
              plate_number: {
                $like: advanceSearch.plate_number_cont,
              },
            });
          }

          if (advanceSearch.event_name_cont) {
            advanceSearchConditions.push({
              "events.config.name": {
                $like: advanceSearch.event_name_cont,
              },
            });
          }

          if (advanceSearch.event_type_cont) {
            advanceSearchConditions.push({
              "events.type": {
                $like: advanceSearch.event_type_cont,
              },
            });
          }

          if (advanceSearch.event_sub_type_cont) {
            advanceSearchConditions.push({
              "events.sub_type": {
                $like: advanceSearch.event_sub_type_cont,
              },
            });
          }

          if (advanceSearch.device_name_cont) {
            advanceSearchConditions.push({
              "events.device_name": {
                $like: advanceSearch.device_name_cont,
              },
            });
          }

          if (advanceSearch.device_imei_cont) {
            advanceSearchConditions.push({
              source_id: {
                $like: advanceSearch.device_imei_cont,
              },
            });
          }

          if (advanceSearch.driver_name_cont) {
            advanceSearchConditions.push({
              "events.driver_name": {
                $like: advanceSearch.driver_name_cont,
              },
            });
          }

          // If we have advanced search conditions, use them
          if (advanceSearchConditions.length > 0) {
            // When advanced search has plate_number_cont, we don't need the main plate_number condition
            // because the advanced search will handle the plate number filtering
            const qParam = encodeURIComponent(
              JSON.stringify({
                $and: advanceSearchConditions,
              })
            );

            return {
              url: `?start_time=${queryParams.startTime}&end_time=${queryParams.endTime}&per_page=${queryParams.perPage}&page=${queryParams.page}&client_id=${queryParams.clientId}&q=${qParam}`,
              method: "get",
            };
          }
        }

        // If search is false then search params are not sent
        let qParam = queryParams?.isAdmin
          ? search && queryParams.q && queryParams.q.trim() !== ""
            ? encodeURIComponent(
                JSON.stringify({
                  $or: [
                    {
                      plate_number: {
                        $like: queryParams.q,
                      },
                    },
                    {
                      "events.type": {
                        $like: queryParams.q,
                      },
                    },
                    {
                      "events.sub_type": {
                        $like: queryParams.q,
                      },
                    },
                    {
                      "events.name": {
                        $like: queryParams.q,
                      },
                    },
                    {
                      "events.config.name": {
                        $like: queryParams.q,
                      },
                    },
                    {
                      "events.status": {
                        $like: queryParams.q,
                      },
                    },
                    {
                      source_id: {
                        $like: queryParams.q,
                      },
                    },
                    {
                      "events.device_name": {
                        $like: queryParams.q,
                      },
                    },
                    {
                      "events.driver_name": {
                        $like: queryParams.q,
                      },
                    },
                  ],
                })
              )
            : null
          : search && queryParams.q && queryParams.q.trim() !== ""
          ? encodeURIComponent(
              JSON.stringify({
                $and: [
                  {
                    plate_number: {
                      $in: queryParams.plate_number,
                    },
                  },
                ],
                $or: [
                  {
                    plate_number: {
                      $like: queryParams.q,
                    },
                  },
                  {
                    "events.type": {
                      $like: queryParams.q,
                    },
                  },
                  {
                    "events.sub_type": {
                      $like: queryParams.q,
                    },
                  },
                  {
                    "events.name": {
                      $like: queryParams.q,
                    },
                  },
                  {
                    "events.config.name": {
                      $like: queryParams.q,
                    },
                  },
                  {
                    "events.status": {
                      $like: queryParams.q,
                    },
                  },
                  {
                    source_id: {
                      $like: queryParams.q,
                    },
                  },
                  {
                    device_name: {
                      $like: queryParams.q,
                    },
                  },
                  {
                    driver_name: {
                      $like: queryParams.q,
                    },
                  },
                ],
              })
            )
          : type === "Vehicles"
          ? encodeURIComponent(
              JSON.stringify({
                $and: [
                  {
                    plate_number: {
                      $in: queryParams.plate_number,
                    },
                  },
                ],
              })
            )
          : encodeURIComponent(
              JSON.stringify({
                $and: [
                  {
                    source_id: {
                      $in: queryParams.source_id,
                    },
                  },
                ],
              })
            );
        return {
          url: `?start_time=${queryParams.startTime}&end_time=${queryParams.endTime}&per_page=${
            queryParams.perPage
          }&page=${queryParams.page}&client_id=${queryParams.clientId}${
            qParam ? `&q=${qParam}` : ""
          }`,
          method: "get",
        };
      },
      keepUnusedDataFor: 2,
      providesTags: ["events"],
    }),
  }),
});

//Raw Data API
export const rawDataApi = createApi({
  name: "rawDataApi",
  reducerPath: "rawDataApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_TRACKING_API + "/raw/devices",
    prepareHeaders: (headers, { getState }) => {
      const authToken = sessionStorage.getItem("fmdp-access-token");
      if (authToken) {
        headers.set("Authorization", authToken);
      }
      return headers;
    },
  }),
  tagTypes: ["rawData"],
  endpoints: (builder) => ({
    getRawData: builder.query({
      query: (queryParams) => {
        return {
          url: `${queryParams?.deviceId}?end_time=${queryParams?.end_time}&start_time=${queryParams?.start_time}&per_page=${queryParams.perPage}&page=${queryParams.page}&decode=${queryParams?.decode}&sort=${queryParams?.sort}`,
          method: "get",
        };
      },
      keepUnusedDataFor: 2,
      providesTags: ["rawData"],
    }),
  }),
});

//Variables API
export const variablesApi = createApi({
  name: "variablesApi",
  reducerPath: "variablesApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_CORE_API_URL + "/variables",
    prepareHeaders: (headers, { getState }) => {
      const authToken = sessionStorage.getItem("fmdp-access-token");
      if (authToken) {
        headers.set("Authorization", authToken);
      }
      return headers;
    },
  }),
  tagTypes: ["variables"],
  endpoints: (builder) => ({
    getVariablesData: builder.query({
      query: (queryParams) => {
        return {
          url: `?per_page=${queryParams.per_page}&page=${queryParams.page}`,
          method: "get",
        };
      },
      keepUnusedDataFor: 2,
      providesTags: ["variables"],
    }),
  }),
});

export const { useGetDevicesTrackingQuery, useGetDeviceDetailsQuery, useGetTrackingDataQuery } =
  trackingApi;
export const { useGetVehiclesTrackingQuery } = trackingVehicleApi;
export const { useGetAddressQuery } = reverseGeoCodingApi;
export const { useGetVehicleRoutingQuery, useLazyGetVehicleRoutingQuery } = vehicleRouteApi;
export const { useGetFormattedVehicleRouteQuery, useLazyGetFormattedVehicleRouteQuery } = formattedVehicleRouteApi;
export const { useGetTripsQuery } = tripsApi;
//Activities API
export const { useGetTripsTrackQuery } = tripsTrackApi;
export const { useGetEventsQuery } = eventsApi;
export const { useGetRawDataQuery } = rawDataApi;
export const { useGetVariablesDataQuery } = variablesApi;
